/**
 * /investigate — free-form prompt investigation surface (live mode).
 *
 * v2.8 session layout:
 *   1. Breadcrumb: ▸ INVESTIGATE (mono caption, dim).
 *   2. Recent strip: 3-5 most-recent investigations as horizontal pills.
 *   3. Prompt input (multiline, 4000-char limit; not autofocused so
 *      Shift+Arrow tab cycling continues to work after landing here).
 *   4. Anchor / overrides in a RetroAccordion (collapsed by default).
 *   5. Submit row: [▸ investigate] + [stop] + [clear] + defaults caption.
 *   6. ReplStream (collapse-by-default tool timeline per v2.4).
 *   7. "Open detail page →" when investigationId is set.
 *   8. DetectiveFooter while status === 'running'.
 *
 * DetectiveProbe is removed from the submit row (replaced by DetectiveFooter).
 * DetectiveProbe.tsx has been deleted.
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  ClarificationPanel,
  type ClarificationAnswer,
} from '@/components/ClarificationPanel';
import { MAX_CLARIFICATION_ROUNDS } from '@/hooks/useInvestigateStream';
import { ProjectFilter } from '@/components/ProjectFilter';
import { RelatedInvestigations } from '@/components/RelatedInvestigations';
import { RetroAccordion } from '@/components/RetroAccordion';
import { ReplStream } from '@/components/ReplStream';
import type { ReplAnchor, ReplStatus, ReplToolCall } from '@/components/ReplStream';
import type { TimelineEntry } from '@/components/InvestigationTimeline';
import { investigate as investigateApi } from '@/helpers/ApiHelper';
import { useInvestigateStream } from '@/hooks/useInvestigateStream';
import type {
  InvestigateRequest,
  InvestigationAnchor,
  InvestigationSummary,
} from '@/types/investigation';
import { formatRelative } from '@/utils/formatRelative';

const CANONICAL_SERVICES = [
  'detect-service',
  'gce-proxy',
  'rabbit-custom',
  'flower',
  'platform',
] as const;

function localToISO(local: string): string | undefined {
  if (!local) return undefined;
  const dt = new Date(local);
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt.toISOString();
}

/**
 * Try to extract a row/event count from a tool result_preview JSON blob.
 */
function extractRowCount(resultPreview: string | null): number | undefined {
  if (!resultPreview) return undefined;
  try {
    const raw = JSON.parse(resultPreview) as Record<string, unknown>;
    const val = raw.rows ?? raw.events_count ?? raw.count ?? raw.total;
    if (typeof val === 'number') return val;
  } catch {
    // not valid JSON — ignore
  }
  return undefined;
}

function adaptTimeline(timeline: TimelineEntry[]): ReplToolCall[] {
  return timeline.map((entry) => {
    const status: ReplToolCall['status'] =
      entry.error !== null
        ? 'error'
        : entry.result_preview === null
          ? 'pending'
          : 'ok';
    return {
      id: entry.call_id,
      tool_name: entry.tool,
      args_summary: JSON.stringify(entry.args ?? {}),
      status,
      duration_ms: entry.latency_ms ?? undefined,
      rows: extractRowCount(entry.result_preview),
      error: entry.error ?? undefined,
    };
  });
}

function adaptAnchor(anchor: InvestigationAnchor | null): ReplAnchor | null {
  if (!anchor) return null;
  return {
    from_iso: anchor.from_ts,
    to_iso: anchor.to_ts,
    services: anchor.services ?? [],
  };
}

function adaptStatus(
  s: 'idle' | 'running' | 'done' | 'failed' | 'awaiting_clarification',
): ReplStatus {
  if (s === 'running') return 'streaming';
  if (s === 'done') return 'done';
  if (s === 'failed') return 'error';
  // 'awaiting_clarification' shouldn't reach the ReplStream (showRepl hides
  // it), but typewise we still need to map it to a ReplStatus.
  return 'idle';
}

/** Truncate prompt to N chars for the pill preview. */
function truncatePrompt(text: string, maxLen = 40): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1) + '…';
}

// ─── Recent investigations strip ─────────────────────────────────────────────

interface RecentStripProps {
  investigations: InvestigationSummary[];
}

function RecentStrip({ investigations }: RecentStripProps): ReactElement | null {
  if (investigations.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        mb: 1,
      }}
      aria-label="Recent investigations"
    >
      {investigations.map((inv) => {
        const shortId = `#${inv.id.slice(0, 4)}`;
        const promptPreview = truncatePrompt(inv.prompt);
        const timeAgo = formatRelative(inv.created_at);

        return (
          <Box
            key={inv.id}
            component={RouterLink}
            to={`/investigations/${inv.id}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '2px',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.6875rem',
              color: 'text.secondary',
              textDecoration: 'none',
              transition: 'border-color 150ms ease, color 150ms ease',
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'text.primary',
              },
            }}
          >
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {shortId}
            </Box>
            <Box component="span" sx={{ color: 'divider' }}>·</Box>
            <Box component="span">{promptPreview}</Box>
            <Box component="span" sx={{ color: 'divider' }}>·</Box>
            <Box component="span"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: theme extension text.faintest not in MUI default typing
              sx={{ color: (theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled }}
            >
              {timeAgo}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function InvestigateLivePage(): ReactElement {
  const [prompt, setPrompt] = useState('');
  const [fromTsLocal, setFromTsLocal] = useState('');
  const [toTsLocal, setToTsLocal] = useState('');
  const [servicesText, setServicesText] = useState('');
  // Registry project_key backing the picker above the services field. Purely
  // UI state — the request body still carries `services`.
  const [projectKey, setProjectKey] = useState('');
  const [recentInvs, setRecentInvs] = useState<InvestigationSummary[]>([]);
  const { state, start, cancel, submitClarification, editPrompt } =
    useInvestigateStream();

  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const isRunning = state.status === 'running';
  const isAwaitingClarification = state.status === 'awaiting_clarification';
  // Project is required — an investigation has to say which project it's about.
  const canSubmit =
    prompt.trim().length > 0 && projectKey !== '' && !isRunning && !isAwaitingClarification;
  const canClear =
    prompt.length > 0 ||
    Boolean(fromTsLocal) ||
    Boolean(toTsLocal) ||
    Boolean(servicesText) ||
    Boolean(projectKey);

  // Fetch recent investigations for the strip (best-effort, non-blocking).
  useEffect(() => {
    let active = true;
    investigateApi
      .list({ page: 1 })
      .then((resp) => {
        if (!active) return;
        setRecentInvs(resp.results.slice(0, 5));
      })
      .catch(() => {
        // Non-critical — strip shows empty if fetch fails
      });
    return () => {
      active = false;
    };
  }, []);

  // Cleanup on unmount.
  useEffect(() => () => cancel(), [cancel]);

  // Dispatch buddy-context events when job state transitions.
  //
  // We key the dedup ref on `${status}:${identifier}` (not just status) so the
  // 'running' dispatch waits for `investigationId` to land via the `anchor`
  // event. Without this, `useInvestigateStream.start()` flips status to
  // 'running' BEFORE the anchor arrives — the buddy-context entry would be
  // added under the literal '__unidentified__' fallback key, but the
  // subsequent 'done' dispatch (once investigationId IS set) would try to
  // remove the real key, leaving the unidentified entry stuck in
  // DetectiveFooter's activeJobs set and the buddy walking forever.
  const prevKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const cur = state.status;
    if (cur !== 'running' && cur !== 'done' && cur !== 'failed') return;
    // Wait for `investigationId` before firing the very first 'running' —
    // otherwise the entry won't match the subsequent 'done'/'failed' cleanup.
    if (cur === 'running' && !state.investigationId) return;
    const identifier = state.investigationId
      ? state.investigationId.slice(0, 8)
      : undefined;
    const key = `${cur}:${identifier ?? '__'}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;
    // `job` carries the full UUID + type so DetectiveFooter's polling
    // reconciler can recover the terminal state if the user navigates
    // away before the stream completes.
    const job = state.investigationId
      ? ({ type: 'investigation', id: state.investigationId } as const)
      : undefined;
    window.dispatchEvent(
      new CustomEvent('sherlock:buddy-context', {
        detail: { kind: 'job', status: cur, identifier, job },
      }),
    );
  }, [state.status, state.investigationId]);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;

    const services = servicesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const body: InvestigateRequest = {
      prompt: prompt.trim(),
      tz,
    };
    const fromIso = localToISO(fromTsLocal);
    const toIso = localToISO(toTsLocal);
    if (fromIso) body.from_ts = fromIso;
    if (toIso) body.to_ts = toIso;
    if (services.length > 0) body.services = services;

    start({ mode: 'live', body });
  }

  function handleClear(): void {
    setPrompt('');
    setFromTsLocal('');
    setToTsLocal('');
    setServicesText('');
    setProjectKey('');
  }

  // While the clarification gate is open we hide the (would-be-empty) repl
  // pane so the panel itself is the only thing competing for attention.
  const showRepl =
    !isAwaitingClarification &&
    (state.status === 'running' ||
      state.status === 'done' ||
      state.status === 'failed' ||
      state.timeline.length > 0 ||
      Boolean(prompt));

  const handleClarificationSubmit = useCallback(
    (answers: Record<number, ClarificationAnswer>, mode: 'augment' | 'skip') => {
      submitClarification(answers, mode);
    },
    [submitClarification],
  );

  const handleClarificationEdit = useCallback(
    (answers: Record<number, ClarificationAnswer>) => {
      const restored = editPrompt(answers);
      if (restored) {
        // Drop the user back at the textarea with the augmented draft so
        // they can hand-edit before resubmitting. The draft includes the
        // original prompt + Q/A pairs (build matches buildAugmentedPrompt
        // in the hook).
        setPrompt(restored.draft);
      }
    },
    [editPrompt],
  );

  // Anchor summary line for the accordion subtitle
  const anchorSummary = (() => {
    const parts: string[] = [];
    if (fromTsLocal) parts.push(fromTsLocal);
    if (toTsLocal) parts.push(`→ ${toTsLocal}`);
    if (servicesText) parts.push(servicesText);
    if (parts.length === 0) parts.push('now · all services');
    return parts.join(' · ');
  })();

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2}>
        {/* v2.8 — Breadcrumb (replaces h4 heading + subtitle) */}
        <Box>
          <Typography
            component="span"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.6875rem',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'text.secondary',
              userSelect: 'none',
            }}
          >
            ▸ INVESTIGATE
          </Typography>
        </Box>

        {/* v2.8 — Recent strip (above the prompt) */}
        <RecentStrip investigations={recentInvs} />

        {/* Prompt form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={2}>
            {/* Prompt textarea */}
            <TextField
              id="investigate-prompt"
              label="What happened?"
              placeholder="e.g. uptime check failed at 6:20 last night, what happened? (shift+enter to submit)"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e as unknown as React.FormEvent);
                }
              }}
              multiline
              minRows={4}
              maxRows={8}
              fullWidth
              disabled={isRunning}
              inputProps={{ maxLength: 4000 }}
            />

            {/* Project — required. Deliberately outside the overrides accordion:
                it's part of stating the question, not an override of a parsed
                default, and a required field can't live behind a collapsed
                panel. No `requires` gate — every registry project must stay
                selectable or a mandatory field would be unsatisfiable for the
                8 projects with no Datadog services mapped. */}
            <ProjectFilter
              value={projectKey}
              onChange={(key, project) => {
                setProjectKey(key);
                // Seeds the services scope; still editable in the accordion.
                setServicesText(project ? project.datadog_services.join(', ') : '');
              }}
              label="Project"
              required
              disabled={isRunning}
              sx={{ minWidth: 260 }}
            />

            {/* v2.8 — Anchor / overrides accordion (collapsed by default) */}
            <RetroAccordion
              title="Anchor / overrides"
              subtitle={anchorSummary}
              defaultOpen={false}
            >
              <Box sx={{ pt: 1, pb: 0.5 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'flex-start' }}
                >
                  <TextField
                    id="investigate-from"
                    label="From (optional)"
                    type="datetime-local"
                    value={fromTsLocal}
                    onChange={(e) => setFromTsLocal(e.target.value)}
                    size="small"
                    disabled={isRunning}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    id="investigate-to"
                    label="To (optional)"
                    type="datetime-local"
                    value={toTsLocal}
                    onChange={(e) => setToTsLocal(e.target.value)}
                    size="small"
                    disabled={isRunning}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    id="investigate-services"
                    label="Services (optional, comma-separated)"
                    placeholder={CANONICAL_SERVICES.join(', ')}
                    value={servicesText}
                    onChange={(e) => setServicesText(e.target.value)}
                    size="small"
                    disabled={isRunning}
                    fullWidth
                    helperText={
                      projectKey
                        ? `Pre-filled from ${projectKey}; edit to narrow the scope`
                        : `Canonical: ${CANONICAL_SERVICES.join(', ')}`
                    }
                  />
                </Stack>
              </Box>
            </RetroAccordion>

            {/* v2.8 — Submit row */}
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Button
                type="submit"
                variant="contained"
                disabled={!canSubmit}
                size="large"
                startIcon={
                  isRunning ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
                sx={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                {isRunning ? 'investigating…' : '▸ investigate'}
              </Button>

              {isRunning && (
                <Button
                  type="button"
                  size="large"
                  variant="text"
                  onClick={cancel}
                  sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'text.secondary' }}
                >
                  stop
                </Button>
              )}

              <Button
                type="button"
                size="large"
                variant="text"
                onClick={handleClear}
                disabled={!canClear || isRunning}
                sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'text.secondary' }}
              >
                clear
              </Button>

              <Box sx={{ flexGrow: 1 }} />

              {/* v2.8 — Right-side action: Open detail page when investigationId set */}
              {state.investigationId && (
                <Button
                  component={RouterLink}
                  to={`/investigations/${state.investigationId}`}
                  size="small"
                  variant="text"
                  sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem' }}
                >
                  Open detail page →
                </Button>
              )}
            </Stack>

            {/* Defaults caption */}
            <Typography
              variant="caption"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
              color={(theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled}
              sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem' }}
            >
              Defaults: timezone {tz} · From/To override the parsed window · up to 10/user/day
            </Typography>
          </Stack>
        </Box>

        {/* Error state */}
        {state.status === 'failed' && state.error && (
          <Alert
            severity={state.error.status === 429 ? 'warning' : 'error'}
            role="alert"
          >
            {state.error.message}
          </Alert>
        )}

        {/* Pre-flight clarification gate (post-Sprint-12). Renders in
            place of the repl pane when the backend returns HTTP 202 with
            clarifying questions. The user answers, skips, or returns to
            the prompt textarea via the panel's actions. */}
        {isAwaitingClarification && state.clarification && (
          <ClarificationPanel
            payload={state.clarification}
            round={state.clarificationRound}
            maxRounds={MAX_CLARIFICATION_ROUNDS}
            onSubmit={handleClarificationSubmit}
            onEdit={handleClarificationEdit}
          />
        )}

        {/* Sprint 12 Phase B — related-investigations chip panel.
            Hides itself when memoryEpisodes is null or empty so the
            live narrative stays the focal point on cold-start runs. */}
        {showRepl && <RelatedInvestigations episodes={state.memoryEpisodes} />}

        {/* v2.8 — ReplStream (collapse-by-default tool timeline per v2.4) */}
        {showRepl && (
          <ReplStream
            prompt={prompt}
            anchor={adaptAnchor(state.anchor)}
            timeline={adaptTimeline(state.timeline)}
            narrative={state.narrative}
            status={adaptStatus(state.status)}
          />
        )}

        {/* Cost / tokens / rounds footer once the run completes. */}
        {state.status === 'done' && (
          <Box>
            <Divider sx={{ mb: 1 }} />
            <Stack
              direction="row"
              spacing={3}
              flexWrap="wrap"
              useFlexGap
              sx={{ rowGap: 1 }}
            >
              <Typography
                variant="caption"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
                color={(theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled}
                sx={{ fontFamily: '"JetBrains Mono", monospace', fontVariantNumeric: 'tabular-nums' }}
              >
                rounds: {state.rounds}
              </Typography>
              <Typography
                variant="caption"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
                color={(theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled}
                sx={{ fontFamily: '"JetBrains Mono", monospace', fontVariantNumeric: 'tabular-nums' }}
              >
                cost: ${state.costUsd.toFixed(4)}
              </Typography>
              <Typography
                variant="caption"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
                color={(theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled}
                sx={{ fontFamily: '"JetBrains Mono", monospace', fontVariantNumeric: 'tabular-nums' }}
              >
                tokens: {state.tokensUsed.toLocaleString()}
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>

    </Container>
  );
}
